/**
 * We need to avoid using the `toString` function on numbers because the benchmarks
 * on it's performance diff greatly depending on the browser being utilized. Whereas
 * the way we are converting it here is significantly fast and more performance across
 * a wide array of browsers.
 *
 * @note in case anything changes in the future we can quickly change it here and affect
 * everything instead of having to update it everywhere.
 *
 * @param value the value to stringify
 * @returns a stringified version of the value provided
 */
export const numberToString = (value: number): string => '' + value;

/**
 * We are storing our dollar amount as cents in the database to avoid floating values.
 * In doing so we need to convert all numbers to an integer that represents the cent
 * value. Taking the value and multiplying it by 100 and then rounding it do that for us.
 *
 * @param value the number to convert to a cents value
 * @returns the newly formatted cents value from the provided number
 */
export const numberToCents = (value: number) => Math.round(value * 100);

/**
 * We are storing dollar amount as cents in the database so we need to convert to
 * a dollar value to show the user properly.
 *
 * @param value the cent value to convert to dollars
 * @returns the newly formatted dollars value from the provided cents
 */
export const centsToDollars = (value: number, place = 2) =>
	roundNumber(value / 100, place);

const DollarRegex = /[()$,]/g;

/**
 * Converts all dollar based values to the number equivalent. This method will persist the
 * decimal places in the dollar amount as well.
 *
 * @example "$12,000.52" will convert to 12000.52
 */
export const dollarToNumber = (value: string) => {
	if (!DollarRegex.test(value)) {
		return stringToNumber(value);
	}

	const convertNegative = value.startsWith('(') && value.endsWith(')');
	const strippedValue = stringToNumber(value.replace(DollarRegex, ''));
	if (isNaN(strippedValue)) return 0;

	// this means that it is negative
	if (convertNegative) return strippedValue * -1;
	return strippedValue;
};

/**
 * We need to unify all instances of our string-to-number workflow to ensure vast
 * performance across the app on all browsers. Converting a string to a number greatly
 * differs based on the browser it's running but one of the always highest achieving
 * functions was `parseFloat` and `parseInt`. Since we are dealing with decimals we need
 * to use `parseFloat`.
 *
 * @note in case anything changes in the future we can quickly change it here and affect
 * everything instead of having to update it everywhere.
 *
 * @note `parseFloat` should work most of the time, however we need to be weary about
 * very large and very small numbers.
 *
 * @note `parseFloat` stops once a string has a character that is no longer signified as a
 * number. For instance "123px" will return 123, and NOT a NaN or error.
 *
 * @param value the string value to convert to a number
 * @returns the converted number based on the string provided
 */
export const stringToNumber = (value: string) => parseFloat(value);

/**
 * This util function is used to unify all of our percentages to have the same formatting. Our
 * percentages are saved to the fourth decimal place instead of two. That way we can have a "25.25%"
 * format for our values. This will allow us to get closer to our calculations when showcasing them
 * in the UI.
 *
 * @note we multiply and divide by 10,000 instead of 100 so we can keep four decimal places instead of
 * the usual two for percentages. This should be consistent throughout the entire app.
 *
 * @param value the value to convert to a percentage
 * @returns the converted percentage
 */
export const numberToPercentage = (value: number, max = 4) =>
	stringToNumber(value.toFixed(max));

/**
 * @param value the value to round to
 * @param place the placement to round the value up to, only if it's greater than this will be rounded to the value provided.
 */
export const roundNumber = (value: number, place = 4) =>
	stringToNumber(value.toFixed(place));

/**
 * Converts a number value to a readable percentage value that the user can understand.
 *
 * @param value the value to transform into a readable percentage value
 * @returns the converted value
 */
export const percentageToValue = (value: number, place = 1) => {
	if (!Number.isFinite(value)) value = 0;
	const multi = value * 100;
	return `${multi.toFixed(place)}%`;
};

/**
 * Capitalizes the first character of the provided value and returns it.
 *
 * @example capitalize("coffee") === "Coffee"
 * @example capitalize("deJean") === "DeJean"
 * @example capitalize("aCTION") === "ACTION"
 *
 * @param value the value to capitalize
 */
export const capitalize = (value: string) =>
	value.charAt(0).toUpperCase() + value.slice(1);

/**
 * Splits a property into separate words based on the casing of the key. This is best used when you need to
 * visualize an object property into the UI and make it human readable.
 *
 * @example `createdAt` becomes "created at".
 * @example `propertyToWords` becomes "property to words"
 *
 * Can be used in conjunction with the `capitalize` function
 *
 * @param value the property key to change into separate words
 * @returns word variant of the initial property
 */
export const propertyToWords = (value: string) =>
	value
		.split(/(?=[A-Z])/)
		.map((v) => v.toLowerCase())
		.join(' ');
